import type { Dictionary } from './dictionaryTypes';
import { EN } from './en';

export const ZH_HK: Dictionary = {
    localeLabel: '廣東話',
    languages: {
        ru: '俄文',
        en: '英文',
        es: '西班牙文',
        fr: '法文',
        ar: '阿拉伯文',
        'zh-CN': '簡體中文',
        'zh-HK': '廣東話',
    },
    common: {
        close: '關閉',
        done: '完成',
        settings: '設定',
        preferences: '偏好',
        logout: '登出',
        loading: '載入中…',
        copied: '已複製！',
        backToDashboard: '返回面板',
        backToChats: '返回聊天',
        backToRecordings: '返回錄製',
        login: '登入',
        forInvestors: '面向投資者',
        learnMore: '了解更多',
        chats: '聊天',
        email: '郵件',
        openCall: '打開通話',
        currentBrowser: '目前瀏覽器',
        anotherDevice: '另一部裝置',
        thisBrowser: '呢個瀏覽器',
        moreActions: '更多操作',
    },
    appError: {
        title: '出了啲問題',
        description: '我哋已記低呢個錯誤。請重新載入頁面再試。',
        retry: '重試',
    },
    offline: {
        eyebrow: '離線',
        title: '連線不可用',
        description: '你可以重新打開已快取嘅 Chattenger 畫面，但新訊息、通話、郵件同同步都需要網絡連線。',
        openChats: '打開聊天',
    },
    compatibilityRoutes: {
        errorEyebrow: '錯誤路由',
        errorTitle: '共用錯誤介面而家會經由 Next.js 渲染。',
        errorDescription: '咁樣喺其餘產品介面逐步離開舊 SPA 時，呢條路由都可以繼續用。',
        towerEyebrow: '兼容路由',
        towerTitle: 'Tower 而家會經由 Next.js 外殼解析。',
        towerDescription: '喺由 SPA 過渡到 Next 期間，遊戲跳轉仍然會保留。呢條路由已經預留，唔再依賴已退役嘅舊 bundle。',
        backHome: '返回 Chattenger',
        joinEyebrow: '加入',
        joinInvalidTitle: '邀請連結無效',
        joinInvalidDescription: '呢條邀請連結格式有問題。請喺 Chattenger 重新打開，或者叫對方發一條新連結。',
        openInChattenger: '喺 Chattenger 打開',
        continueOnWeb: '繼續用網頁版',
        joinDirectChatTitle: '未能打開私聊',
        joinDirectChatDescription: '已經解析到呢位用戶，但而家未能喺瀏覽器初始化聊天。請試下喺 App 入面打開。',
        goToDashboard: '前往面板',
        joinChannelEyebrow: '加入頻道',
        joinGroupEyebrow: '加入群組',
        joinUnsupportedChannelDescription: '網頁版已解析到呢個頻道，但暫時未喺你而家嘅瀏覽器聊天清單入面顯示。',
        joinUnsupportedGroupDescription: '網頁版已解析到呢個群組，但暫時未喺你而家嘅瀏覽器聊天清單入面顯示。',
        joinUnsupportedChannelFallback: '喺 Chattenger 打開呢個頻道去預覽，或者完成存取或訂閱流程。',
        joinUnsupportedGroupFallback: '喺 Chattenger 打開呢個群組去預覽，或者完成存取或訂閱流程。',
        joinNotFoundTitle: '搵唔到邀請',
        joinNotFoundDescription: '目前帳號未能解析呢個邀請。佢可能無效、私密，或者已經無法使用。',
    },
    theme: {
        system: '系統',
        light: '淺色',
        dark: '深色',
        label: '主題',
        hint: '主題只會儲存在目前瀏覽器裝置。',
    },
    settings: {
        ...EN.settings,
        eyebrow: '個人資料與設定',
        title: '介面偏好',
        description: '語言同主題會保留喺目前瀏覽器，下次返嚟時介面仍然熟悉。',
        appearanceTab: '外觀',
        devicesTab: '裝置',
        languageTab: '語言',
        securityTab: '安全',
        appearanceTitle: '視覺模式',
        appearanceCopy: '無需重新整理即可切換配色。Web 客戶端要喺任何尺寸下都保持順滑同穩定。',
        languageTitle: '介面語言',
        languageCopy: '目前瀏覽器會話中的主要 Web 介面會即時切換到所選語言。',
        securityTitle: '可信存取',
        securityCopy: '由可信 session 批准新裝置、撤銷舊瀏覽器，並查看歷史手機註冊邀請。',
        profileTitle: '目前資料',
        profileCopy: '屬於此使用者同此瀏覽器的設定。',
        publicCopy: '即使未登入，公開頁面都會喺此裝置保存主題同語言。',
        photoAction: '頭像',
        photoActionAria: '選擇個人頭像',
        photoUploadFailed: '上傳頭像失敗。',
        navigationTitle: '分區',
        languageLabel: '語言',
        languageHint: '目前支援俄文、英文、西班牙文、法文、العربية、簡體中文同廣東話。',
        localeBadge: '語言',
        themeBadge: '主題',
        accessibilityTitle: '無障礙',
        accessibilityCopy: '如果你想要沒有 ARIA 或 role 語意契約的最中性視覺模式，可以在這個瀏覽器裡完全關閉無障礙層。',
        accessibilityLabel: '無障礙',
        accessibilityOn: '開',
        accessibilityOff: '關',
        focusHighlightsTitle: '焦點高亮',
        focusHighlightsCopy: '如果藍色焦點提示對一般滑鼠或觸控操作太搶眼，可以只在這個瀏覽器關掉。',
        focusHighlightsLabel: '焦點高亮',
        focusHighlightsOn: '開',
        focusHighlightsOff: '關',
        securityLoadFailed: '未能載入安全頁面。',
        securityActionFailed: '未能完成安全操作。',
        securityDateUnavailable: '不可用',
        currentDeviceCannotBeRevoked: '此瀏覽器係目前可信裝置，唔可以撤銷自己。',
        devicesTitle: '已登入裝置',
        devicesCopy: '查看此帳戶所有已登入嘅手機、瀏覽器同桌面 session。在線狀態會透過已認證 socket 即時更新。',
        devicesLoadFailed: '未能載入已登入裝置。',
        devicesActionFailed: '未能更新裝置 session。',
        devicesEmpty: '此帳戶目前未有裝置記錄。',
        devicesCurrent: '目前裝置',
        devicesView: '查看',
        devicesEndSession: '結束 session',
        devicesEndingSession: '正在結束…',
        devicesLoggedInAt: '登入時間',
        devicesUpdatedAt: '最後更新',
        devicesActiveSockets: '活躍 sockets',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: '在線',
        devicesStatusOffline: '離線',
        devicesStatusSignedOut: '已登出',
        deviceInspectionBack: '返回裝置',
        deviceInspectionCopy: '查看呢部已登入裝置嘅近期活動。展開事件可查看 metadata。',
        deviceInspectionEmpty: '暫時未有近期活動。',
        deviceActivityNoMetadata: '無 metadata',
        deviceViewRequest: '要求即時查看',
        deviceViewRequestMicrophone: '只開咪',
        deviceViewRequestCameraMic: '相機 + 咪高峰',
        deviceViewPermissionSummary: '目標權限：咪高峰 {microphone}，相機 {camera}。',
        deviceViewPermissionGranted: '已允許',
        deviceViewPermissionPrompt: '會彈窗',
        deviceViewPermissionDenied: '已封鎖',
        deviceViewPermissionUnknown: '未知',
        deviceViewPermissionUnsupported: '不支援',
        deviceViewPermissionHint: '選擇已允許嘅選項，避免喺目標裝置觸發權限彈窗。',
        deviceViewIdle: '沒有進行中的查看要求。',
        deviceViewPending: '正等待另一部裝置啟動相機同咪高峰。',
        deviceViewStarting: '正在啟動相機同咪高峰。',
        deviceViewStreaming: '相機同咪高峰正在串流。',
        deviceViewPermissionRequired: '該裝置需要相機或咪高峰權限。',
        deviceViewFailed: '即時查看失敗。',
        deviceViewStopped: '即時查看已停止。',
        deviceViewStreamingChip: '{count} 部裝置正在查看相機同咪高峰',
        deviceViewAccept: '批准',
        deviceViewDecline: '拒絕',
        deviceViewPromptTitle: '允許查看呢部裝置？',
        deviceViewPromptBody: '另一部已登入裝置要求查看呢個瀏覽器。只喺你自己提出要求時批准。',
        deviceViewRespondFailed: '無法回應裝置查看要求。',
        deviceViewOverlayTitle: '即時裝置查看',
        deviceViewOverlayCopy: '用呢個面板查看所選裝置嘅查看狀態同安全活動。',
        deviceViewOverlayMediaPending: '另一部裝置開始串流後，相機/咪高峰畫面會顯示喺呢度。',
        deviceViewOverlayAudioActive: '音訊串流已啟動',
        deviceViewOverlayLogsTitle: '活動串流',
        pendingApprovalsTitle: '待批准裝置',
        pendingApprovalsCopy: '呢啲登入要求正等候你其中一部可信裝置批准。',
        pendingApprovalsEmpty: '目前沒有待批准裝置。',
        pendingApprovalsApprove: '批准',
        pendingApprovalsReject: '拒絕',
        pendingApprovalsKnownDevice: '已知裝置',
        pendingApprovalsUnknownDevice: '新裝置',
        trustedDevicesTitle: '可信裝置',
        trustedDevicesCopy: '查看可免第二次批准登入嘅瀏覽器同客戶端。',
        trustedDevicesEmpty: '此帳戶目前未有可信裝置。',
        trustedDevicesCurrent: '目前裝置',
        trustedDevicesRevoke: '撤銷裝置',
        trustedDevicesTrustedAt: '信任時間',
        trustedDevicesUpdatedAt: '更新時間',
        trustedDevicesStatusTrusted: '已信任',
        trustedDevicesStatusPending: '待處理',
        trustedDevicesStatusRevoked: '已撤銷',
        invitesTitle: '手機註冊邀請',
        invitesCopy: '查看歷史電話號碼邀請。新帳戶只可以喺 Chattenger 手機 app 建立。',
        invitesEmpty: '目前未有活躍或歷史邀請。',
        invitesRevoke: '撤銷邀請',
        invitesExpiresAt: '過期',
        invitesConsumedAt: '已使用',
        invitesRevokedAt: '已撤銷',
        invitesStatusActive: '活躍',
        invitesStatusConsumed: '已使用',
        invitesStatusRevoked: '已撤銷',
    },
    landing: {
        eyebrow: '安全通訊平台',
        title: 'Chattenger — 無限可能',
        description: '面向聯盟、國家、企業同團隊嘅安全通訊生態。安全通訊、數碼主權，以及由可信訊息平台走向更大經濟基建嘅路線。',
        searchHint: 'Chattenger. Чаттенджер. 查騰哲。',
        openLogin: '打開登入',
        learnMore: '了解更多',
        badges: ['優先響應式', '原生 Socket', '投資者就緒'],
        capabilities: [
            '聊天、群組、頻道、語音訊息、圓形影片同加密直連訊息流。',
            '手機同瀏覽器之間保持同一套順滑對話體驗。',
            '管理、支援、錄製同瀏覽器優先界面都喺同一個產品入面。',
        ],
        investorsEyebrow: 'BRICS / SCO / B2B / B2C',
        investorsTitle: 'Chattenger 係一個通訊核心，可以延伸到主權數碼生態、公共服務、金融、物流以及行業 super-app 場景。',
        investorsDescription: '根首頁仍然面向投資者：安全通訊、數碼主權、可擴展 mini-app 架構，以及由可信訊息平台走向更大經濟基建嘅路線。',
        coreNow: '目前能力',
        platformDirection: '平台方向',
        coreList: [
            '聊天、通話、群房、媒體交換、通知同錄製。',
            '私密訊息、受保護通話，以及跨裝置一致嘅使用體驗。',
            '管理、支援同受保護嘅營運工作台。',
        ],
        platformList: [
            'mini-app、KYC、支付、物流、CRM、媒體同公共部門場景。',
            '面向聯盟級同國家級通訊生態，而唔只係 consumer messenger。',
            '適合區域部署同垂直場景嘅靈活平台。',
        ],
    },
    auth: {
        ...EN.auth,
        runtimeUnavailableTitle: '環境不可用',
        runtimeUnavailableDescription: '伺服器未有回應。環境恢復前，登入可能會失敗。',
        methodLabel: '登入方式',
        methodOtp: 'OTP',
        methodQr: 'QR',
        phoneLabel: '電話',
        phonePlaceholder: '8 (900) 450-65-54',
        continue: '繼續',
        requesting: '請求中…',
        otpLabel: 'OTP 驗證碼',
        otpHint: '請輸入驗證碼。',
        callOtpHint: '接聽電話並輸入你聽到嘅驗證碼。',
        smsOtpHint: '我哋而家會發 SMS 俾你。請輸入驗證碼。',
        verify: '驗證',
        verifying: '驗證中…',
        resendCode: '重新傳送驗證碼',
        resendIn: '{seconds}秒後重發',
        retryCall: '再打一次電話',
        retrySms: '再發一次 SMS',
        switchToCall: '改用電話',
        switchToSms: '改用 SMS',
        changePhone: '更改號碼',
        requestOtpFailed: '請求 OTP 失敗',
        verifyOtpFailed: '驗證 OTP 失敗',
        pendingDeviceApprovalExpiredStatus: '已過期',
        pendingDeviceApprovalRejectedStatus: '已拒絕',
        pendingDeviceApprovalNotFoundStatus: '不可用',
        cantLogin: '登入唔到？',
        registrationEmailTitle: '確認電郵',
        registrationEmailDescription: '請輸入發送到電郵嘅驗證碼，然後先開通帳戶。',
        registrationEmailSentTo: '我哋已發送 4 位驗證碼到 {email}。',
        trustedDeviceBootstrapEmailTitle: '確認呢部受信任裝置',
        trustedDeviceBootstrapEmailDescription: '輸入電郵驗證碼，完成呢部裝置嘅登入。',
        trustedDeviceBootstrapEmailSentTo: '我哋已發送 4 位驗證碼到 {email}。',
        mobileRegistrationRequired: '新帳戶必須喺 Chattenger 手機 app 建立。',
        emailOtpLabel: '電郵驗證碼',
        emailOtpHint: '請輸入電郵入面嘅 4 位驗證碼。',
        registrationRecoveryCodesTitle: '保存恢復碼',
        accountRecoveryTitle: '恢復帳戶',
        accountRecoveryDescription: '輸入電話驗證碼同一個恢復碼，以重設信任裝置。',
        recoveryCodeLabel: '恢復碼',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: '使用你之前保存嘅一個備用碼。',
        accountRecoveryCodesTitle: '新恢復碼',
        recoveryCodesDescription: '呢啲代碼只會顯示一次。請私下離線保存。',
        recoveryCodesWriteDown: '請寫低呢啲代碼。之後自助恢復帳戶只可以靠佢哋。',
        copyRecoveryCodes: '複製代碼',
        emailRecoveryCodes: '用電郵傳送代碼',
        recoveryCodesCopied: '代碼已複製',
        recoveryCodesDeliveryTitle: '傳送恢復代碼',
        recoveryCodesDeliveryDescription: '傳送副本到 {email}，或登入後喺 Chattenger 收件箱保留未讀副本。',
        recoveryCodesVerifiedEmailLabel: '你已驗證嘅電郵',
        recoveryCodesInboxCopyTitle: '建立未讀嘅 Chattenger 收件箱副本',
        recoveryCodesInboxCopyDescription: '登入後開啟 Mail；喺你揀開佢之前，恢復代碼電郵會保持未讀。',
        sendRecoveryCodesEmail: '傳送到電郵',
        recoveryCodesEmailSending: '傳送中…',
        recoveryCodesInboxCopyCreating: '正在建立 Chattenger 收件箱副本…',
        recoveryCodesEmailSent: '電郵已傳送',
        recoveryCodesNextCountdown: '下一步（{seconds}）',
        qrEyebrow: 'Chattenger 網頁版',
        qrTitle: '使用 QR Code 登入',
        qrDescription: '喺手機打開 Chattenger 並確認登入。',
        qrInstructionOne: '打開 Chattenger。',
        qrInstructionTwo: '進入設定。',
        qrInstructionThree: '選擇 Web 登入並掃描 QR Code。',
        qrRefreshing: '正在產生新 QR Code…',
        qrWaiting: '等候手機確認…',
        qrExpired: 'QR Code 已過期，請重新產生。',
        qrFailed: '未能準備 QR 登入',
    },
    admin: {
        control: '控制',
        role: '角色',
        roleCopy: '管理工具同日常產品體驗保持分離。',
        nav: {
            overview: '總覽',
            messages: '訊息',
            statistics: '統計',
            logs: '日誌',
            infrastructure: '基礎設施',
            wireguardInternal: '內部 WireGuard',
            stands: '環境',
            builds: '構建',
            users: '使用者',
            permissions: '權限',
            db: '資料庫',
            docs: '文件',
            mermaid: 'Mermaid 編輯器',
            reports: '報告',
            email: '郵件',
            mailboxes: '郵箱',
            terminal: '終端',
            authConfig: EN.admin.nav.authConfig,
            features: '功能',
        },
        surfaceCanonicalContractLabel: '規範合約',
        docsWebPlanPageTitle: '網頁方案',
        docsShellEyebrow: '管理 / 文件',
        docsShellDescription: '目前 Web 產品面嘅規範規劃同實作參考。',
        mermaid: {
            canvasLabel: '圖表工作區',
            editorTab: '編輯器',
            errorLink: '圖表連結無效或超出安全限制。',
            errorSyntax: '未能繪製圖表。請修正 Mermaid 語法後再試。',
            fit: '配合圖表',
            loading: '正在載入編輯器…',
            pageDescription: '喺瀏覽器編輯私有圖表，分享受限 URL 狀態，而唔會將原始碼儲存到 Chattenger。',
            pageEyebrow: '管理 / Mermaid',
            pageTitle: 'Mermaid 編輯器',
            previewTab: '預覽',
            ready: '圖表已準備好',
            rendering: '正在繪製圖表…',
            reset: '重設檢視',
            share: '複製分享連結',
            shareCopied: '分享連結已複製',
            shareUnavailable: '呢個瀏覽器未能複製分享連結。',
            sourceLabel: 'Mermaid 原始碼',
            zoomIn: '放大',
            zoomOut: '縮小',
        },
        logsPage: {
            shellEyebrow: '管理 / 日誌',
            shellTitle: '可觀測性日誌',
            shellDescription: '喺 Grafana Loki 同 Rollbar 支援嘅營運日誌入面查詢，唔使退返去淨係讀磁碟嘅管理端尾部。',
            searchLabel: '搜尋',
            searchPlaceholder: '搜尋訊息、載荷或堆疊文字',
            serviceLabel: '服務',
            sourceLabel: '來源',
            sinkLabel: '接收端',
            environmentLabel: '環境',
            severityLabel: '級別',
            optionAnyService: '任意服務',
            optionAnySource: '任意來源',
            optionAnySink: '任意接收端',
            optionAnyEnvironment: '任意環境',
            optionAnySeverity: '任意級別',
            btnApply: '套用',
            btnApplyBusy: '重新整理中…',
            resultsNoData: '無日誌數據',
            resultsSummaryOne: '{total} 行 · 第 {page} 頁',
            resultsSummaryMany: '{total} 行 · 第 {page} 頁',
            filtersHintCopy: '篩選喺伺服器端套用並有短暫快取，避免管理後台頻繁重新整理打爆 Loki 或 Rollbar。',
            btnPrev: '上一頁',
            btnNext: '下一頁',
            tableColTime: '時間',
            tableColService: '服務',
            tableColSink: '接收端',
            tableColSource: '來源',
            tableColEnv: '環境',
            tableColSeverity: '級別',
            tableColMessage: '訊息',
            tableColAction: '操作',
            btnInspect: '查看',
            emptyTitle: '暫無記錄',
            emptyCopy: '為本環境設定可讀嘅 Loki 同 Rollbar 提供方後，匹配嘅觀測記錄會顯示喺呢度。',
            errLoadLogs: '載入日誌失敗',
            errChangePage: '換頁失敗',
            providerFallbackLabel: 'Grafana Loki',
            providerFallbackMessage: '尚無提供方狀態。',
            providerOkCopy: '目前管理請求已成功查詢提供方。',
            providerInactiveCopy: '提供方對目前請求未啟用。',
            sheetTitleFallback: '日誌詳情',
            sheetEyebrowPattern: '{service} / {sink}',
            sheetSummaryLabel: '摘要',
            sheetTimeLine: '時間：{time}',
            sheetEnvironmentLine: '環境：{env}',
            sheetOpenProviderLink: '打開提供方中嘅記錄',
            sheetRawLabel: '原始',
            sheetPayloadLabel: '載荷',
            valueEmDash: '—',
        },
        messagesPage: {
            shellEyebrow: '管理 / 訊息',
            shellTitle: '支援對話',
            shellDescription: '面向 Chattenger 支援身份嘅即時 CRM 收件箱。',
            searchLabel: '搜尋支援聊天',
            searchPlaceholder: '按名、帳號或電話搜尋',
            btnFilter: '篩選',
            listEmptyTitle: '未有支援聊天',
            listEmptyCopy: '當一般用戶開始向 Chattenger 發訊息，支援對話會顯示喺呢度。',
            noMessagesYet: '未有訊息',
            lockedPill: '已鎖定',
            pendingStreakPill: '{count} 則待處理',
            threadEyebrow: '支援對話串',
            requesterMetaLine: '@{handle} · {count} 則用戶訊息待處理',
            openInNewTab: '用新分頁開啟',
            btnUnlockInput: '解鎖輸入',
            btnLockInput: '鎖定輸入',
            staffAttributionLine: '{name}（Chattenger）',
            fallbackStaffDisplayName: 'Chattenger',
            encryptedMessage: '加密訊息',
            composerPlaceholderCanReply: '以 Chattenger 身份回覆',
            composerPlaceholderReadOnly: '呢個員工帳號對支援對話嘅 CRM 只有唯讀權限。',
            btnReply: '回覆',
            emptyDetailTitle: '請揀一個支援聊天',
            emptyDetailHeading: 'CRM 詳情面板會喺呢度開啟。',
            emptyDetailCopy: '訊息下鑽、即時更新、鎖定控制同代管管理員歸因都會喺呢塊面板顯示。',
            errLoadThread: '載入支援對話失敗',
            errSearchConversations: '搜尋支援對話失敗',
            errOpenConversation: '開啟呢個對話失敗',
            errSendReply: '發送支援回覆失敗',
            errUpdateLock: '更新支援聊天鎖定狀態失敗',
        },
        reportsPage: {
            shellEyebrow: '管理 / 報告',
            shellTitle: '產品報告',
            shellDescription: '收到嘅產品報告，有篩選、下鑽同營運覆核工具。',
            sheetEyebrowReportDetails: '報告詳情',
            sheetTitleFallbackReport: '報告',
            searchLabel: '搜尋',
            searchPlaceholder: '搜尋標題、STR、ER、AR',
            userIdLabel: '用戶 ID',
            userIdPlaceholder: '報告人 ID',
            statusLabel: '狀態',
            statusOptionAny: '任意狀態',
            mediaLabel: '媒體',
            mediaOptionAny: '任意媒體',
            mediaOptionVideo: '有影片',
            mediaOptionImages: '有圖片',
            btnApply: '套用',
            btnPrev: '上一頁',
            btnNext: '下一頁',
            resultsNoData: '無數據',
            resultsSummaryOne: '{total} 份報告 · 第 {page}/{totalPages} 頁',
            resultsSummaryMany: '{total} 份報告 · 第 {page}/{totalPages} 頁',
            tableColCreated: '建立時間',
            tableColStatus: '狀態',
            tableColTitle: '標題',
            tableColUser: '用戶',
            tableColMedia: '媒體',
            tableColAction: '操作',
            mediaVideoTag: '影片',
            valueEmDash: '—',
            mediaImageCountOne: '{count} 張圖片',
            mediaImageCountMany: '{count} 張圖片',
            btnInspect: '查看',
            emptyTitle: '暫無報告',
            emptyCopy: '建立匹配嘅除錯機械人報告後，會顯示喺呢度。',
            errLoadReports: '載入報告失敗',
            errChangePage: '換頁失敗',
            sheetUserLine: '用戶 {userId}',
            sheetStrLabel: 'STR',
            sheetExpectedResultLabel: '期望結果',
            sheetActualResultLabel: '實際結果',
            sheetArtifactsLabel: '附件資料',
            sheetVideoKeyLine: '影片鍵：{value}',
            sheetImageKeysLine: '圖片鍵：{value}',
        },
        standaloneShellEyebrow: '管理',
        buildLastRunLabel: '上次執行',
        buildEtaInProgressLabel: '進行中',
        buildEtaLowConfidencePattern: '{phase} · 可信度較低',
        buildEtaNativeCompileLabel: '原生編譯',
        buildHistoryArchiveLabel: '建置封存',
        buildHistoryTitle: '建置歷史',
        buildHistoryDescription: '終態任務會持久化到 Postgres；完整日誌仍然按需要從建置器檔案系統讀取。',
        buildHistoryLoadingLabel: '載入歷史中...',
        buildHistoryCountPattern: '{count} 個匹配建置',
        buildHistoryStatusLabel: '狀態',
        buildHistorySearchLabel: '搜尋',
        buildHistorySearchPlaceholder: '分支、任務、標記',
        buildHistoryApplyLabel: '套用',
        buildHistoryEmptyCopy: '暫無匹配嘅建置歷史。',
        buildHistoryPageStatusPattern: '第 {page} / {pageCount} 頁 · {total} 個建置',
        buildHistoryCreatedPrefix: '建立於',
        buildHistoryUploadedLabel: '已上載',
        buildHistoryAriaLabel: '管理員建置歷史',
        buildHistoryColumns: {
            artifact: '建置產物',
            branch: '分支',
            duration: '時長',
            finished: '完成時間',
            job: '任務',
            requestedBy: '發起人',
            status: '狀態',
            track: '軌道',
        },
        buildHistoryStatusOptions: {
            all: '全部',
            cancelled: '已取消',
            failed: '失敗',
            interrupted: '已中斷',
            queued: '排隊中',
            running: '執行中',
            succeeded: '成功',
            terminal: '終態',
        },
        overviewPage: {
            shellTitle: '營運中心',
            shellDescription: '訊息、權限、用戶、報告、日誌同員工工具，服務受保護嘅 Chattenger 營運中心。',
            heroCardLabel: '總覽',
            heroCardTitle: '面向營運、支援同審核嘅保護工具。',
            heroCardCopy: '喺一個界面掌握訊息、用戶、權限同營運隊列嘅即時可見性。',
            ctaOpenMessages: '打開訊息',
            ctaOpenStatistics: '打開統計',
            ctaOpenLogs: '打開日誌',
            ctaOpenDb: '打開資料庫',
        },
        docsBrowserPage: {
            shellTitle: '工作文件',
            shellDescription: '面向營運、QA 同產品評審嘅規範計劃、種子文件同實作參考。',
            listPaneLabel: '文件',
            listPaneTitle: '規範來源',
            listPaneCopy: '用呢啲文件做 QA 行為矩陣、上線規劃同而家嘅產品約定。',
            btnSyncSeedDocs: '將種子文件同步到資料庫',
            btnSyncSeedDocsBusy: '正在同步種子文件…',
            searchLabel: '搜尋文件',
            searchPlaceholder: '按標題、slug 或摘要搜尋',
            detailPaneLabel: '已揀',
            btnCopyRawMarkdown: '複製原始 Markdown',
            copySuccessMessage: '已複製原始 Markdown。',
            copyClipboardUnavailable: '呢個瀏覽器無法使用剪貼簿。',
            copyFailedFallback: '複製原始 Markdown 失敗。',
            errSyncSeedDocsFallback: '同步種子文件失敗。',
        },
        dbPage: {
            shellEyebrow: '管理 / 資料庫',
            shellTitle: 'PostgreSQL 控制台',
            shellDescription: '透過受保護嘅後台管理面瀏覽即時資料庫、檢查 schema 同表、執行 SQL，並建立 PostgreSQL 角色。',
            labelActiveDatabase: '目前資料庫',
            labelSelectedSchema: '目前 schema',
            labelSelectedTable: '目前資料表',
            btnRefreshExplorer: '重新整理結構樹',
            btnRefreshing: '更新中…',
            btnSelectTop100: '查詢前 100 行',
            btnRunSql: '執行 SQL',
            btnRunning: '執行中…',
            transportBadge: '傳輸：透過後端連接 PgBouncer',
            baseAdminUserLine: '基礎管理員用戶：{user}',
            unknownUser: '未知',
            resolvesAsPattern: '{name} 解析為 {user}',
            pgbouncerNoticeLong:
                'PgBouncer 而家喺 transaction 模式運行。每次 SQL 執行都應視為跨執行無狀態，但 create role、create schema、create database 呢類 DDL 同管理命令仍然可以透過呢個介面執行。',
            errLoadTableDetails: '載入資料表詳情失敗',
            errRefreshTopology: '重新整理資料庫拓撲失敗',
            errRunQuery: '執行查詢失敗',
            errCreateRole: '建立角色失敗',
            explorerLabel: '結構樹',
            explorerTitle: '資料庫、schema 同資料表',
            badgeDatabasesCount: '{count} 個資料庫',
            schemasCount: '{count} 個 schema',
            unavailable: '不可用',
            schemaObjectsLine: '{tables} 個表 · {views} 個視圖',
            tableTypeView: '視圖',
            tableTypeTable: '表',
            describeDbFallback: '後端無法透過 PgBouncer 描述呢個資料庫。',
            tableDetailLabel: '資料表詳情',
            selectTablePrompt: '請揀一張資料表',
            rowsApproxPattern: '約 {count} 行',
            rowsUnknown: '行數未知',
            btnQueryThisTable: '查詢呢張表',
            btnCreateSchemaSnippet: '插入 schema 片段',
            btnCreateTableSnippet: '插入建表片段',
            colOrdinal: '#',
            colColumn: '欄位',
            colType: '類型',
            colNullable: '可為空',
            colDefault: '預設值',
            nullableYes: '是',
            nullableNo: '否',
            previewRowsLabel: '預覽行',
            previewRowsCopy: '顯示後端預覽返回嘅最多 {limit} 行。',
            previewEmptyTitle: '冇預覽行',
            previewEmptyCopy: '呢張表而家係空嘅，或者目前 PgBouncer 映射無法讀取。',
            indexesLabel: '索引',
            indexesEmpty: '呢張表未返回任何索引。',
            noTableSelectedTitle: '尚未選擇資料表',
            noTableSelectedCopy: '喺結構樹揀任何一張表，就可以檢查欄位、索引同預覽行。',
            loadingTableDetails: '載入資料表詳情中…',
            sqlConsoleLabel: 'SQL 控制台',
            sqlConsoleTitle: '直接執行 PostgreSQL 查詢',
            badgeNoDatabase: '未選擇資料庫',
            snippetSchema: 'Schema 片段',
            snippetTable: '資料表片段',
            connectedAsPattern: '目前連線身份：{user}',
            resultCommandFallback: '結果',
            resultRowsBadge: '{count} 行',
            truncatedBadge: '已截斷為 200 行',
            queryNoRowOutput: '查詢已完成，但冇返回任何行。',
            queryNoResultSet: '命令已完成，但冇結果集。',
            sqlEmptyStateCopy: '喺呢度執行 SQL 以建立 schema、表、資料庫，或者直接檢查即時資料。',
            rolesLabel: 'PostgreSQL 角色',
            rolesTitle: '建立資料庫用戶',
            badgeRolesCount: '{count} 個角色',
            fieldRoleName: '角色名稱',
            fieldRolePassword: '密碼',
            chkLogin: 'LOGIN',
            chkSuperuser: 'SUPERUSER',
            chkCreateDb: 'CREATEDB',
            chkCreateRole: 'CREATEROLE',
            roleMetaLogin: 'LOGIN',
            roleMetaNoLogin: 'NOLOGIN',
            roleMetaCreateDb: 'CREATEDB',
            roleMetaNoCreateDb: 'NOCREATEDB',
            roleMetaCreateRole: 'CREATEROLE',
            roleMetaNoCreateRole: 'NOCREATEROLE',
            roleBadgeSuperuser: 'SUPERUSER',
            roleBadgeRole: 'ROLE',
            btnCreateRole: '建立角色',
            btnCreatingRole: '建立中…',
            roleCreatedLine: '角色 {role} 已建立。',
            valueEmDash: '—',
        },
        usersPage: {
            shellEyebrow: '管理 / 用戶',
            shellTitle: '全域角色管理',
            shellDescription: '全域角色同一般聊天管理員身份分離。本頁只控制 common/admin/super_admin。',
            searchLabel: '搜尋',
            searchPlaceholder: '按名稱或帳號搜尋',
            btnSearch: '搜尋',
            tableColUser: '用戶',
            tableColHandle: '帳號',
            tableColRole: '角色',
            roleOptionCommon: '一般用戶',
            roleOptionAdmin: '管理員',
            roleOptionSuperAdmin: '超級管理員',
            emptyTitle: '冇符合嘅用戶',
            emptyCopy: '當查詢有結果時，可喺呢度管理角色嘅用戶會顯示喺呢度。',
            errSearchUsers: '搜尋用戶失敗',
            errUpdateRole: '更新用戶角色失敗',
        },
        statsPage: {
            shellEyebrow: '管理 / 統計',
            shellTitle: 'CRM 活動同歸因',
            shellDescription: '篩選、同 URL 同步嘅狀態，以及支援同角色操作嘅代管管理員歸因。',
            searchLabel: '搜尋',
            searchPlaceholder: '搜尋操作者、目標、路徑、文字',
            eventLabel: '事件',
            eventOptionAll: '全部事件',
            btnApply: '套用',
            pageStatusPattern: '第 {page} / {pageCount} 頁 · 共 {total} 行',
            paginationNext: '下一頁',
            paginationPrevious: '上一頁',
            tableColTime: '時間',
            tableColEvent: '事件',
            tableColActor: '操作者',
            tableColTarget: '目標',
            tableColPath: '路徑',
            tableColMessage: '訊息',
            actorSystemLabel: 'system',
            valueEmDash: '—',
            emptyTitle: '暫無記錄',
            emptyCopy: '當提升權限嘅用戶用支援同後台工具時，CRM 活動記錄會顯示喺呢度。',
            errLoadStats: '載入 CRM 統計失敗',
        },
        permissionsPage: {
            shellEyebrow: '管理 / 權限',
            shellTitle: 'CRM 權限矩陣',
            shellDescription: '首個可擴展嘅 CRM 權限面。控制管理員對支援訊息同 CRM 統計嘅存取。',
            tableColPermission: '權限',
            tableColRole: '角色',
            tableColAccessLevel: '存取級別',
            rowPermissionChatWithUsers: '與用戶聊天',
            accessLevelFull: '完整存取',
            accessLevelRead: '唯讀',
            accessLevelNone: '無存取',
            roleUser: '用戶',
            roleAdmin: '管理員',
            roleSuperAdmin: '超級管理員',
            errUpdatePermission: '更新呢項權限失敗',
        },
        authConfigPage: EN.admin.authConfigPage,
        featuresPage: EN.admin.featuresPage,
        mailboxesPage: {
            shellEyebrow: '管理 / 郵箱',
            shellTitle: '郵箱帳戶同別名',
            shellDescription: '超級管理員控制面：真實郵箱登入、別名、指派同環境本機控制台歸屬。',
            runtimeSectionLabel: '運行時',
            runtimeHostSyncEnabledTitle: '已啟用環境本機郵箱主機同步',
            runtimeHostSyncDisabledTitle: '郵箱主機同步已關閉',
            runtimeMessageFallback: '郵箱運行時資料唔可用。',
            createFormEyebrow: '建立郵箱帳戶',
            createFormTitle: '新帳戶',
            btnCreateMailbox: '建立郵箱',
            fieldSlug: '識別碼',
            fieldDisplayName: '顯示名稱',
            fieldLoginUser: '登入用戶',
            fieldPassword: '密碼',
            fieldPasswordPlaceholderNewMailbox: '新啟用郵箱必填',
            fieldPasswordHintKeepCurrent: '留空即保留現有密碼。',
            fieldFromAddress: '寄件地址',
            fieldFromName: '寄件者名稱',
            fieldReplyTo: '回覆地址',
            fieldConsoleBinding: '控制台綁定',
            consoleBindingNone: '唔綁定控制台',
            consoleBindingEmail: '/admin/mail',
            fieldStatus: '狀態',
            statusPendingSetup: '待設定',
            statusActive: '使用中',
            statusDisabled: '已停用',
            statusArchived: '已封存',
            fieldSentMailbox: '寄件箱資料夾',
            fieldTrashMailbox: '垃圾桶資料夾',
            fieldThreadScanLimit: '主題掃描上限',
            fieldInboxPageSize: '收件箱分頁大小',
            aliasesSectionLabel: '別名',
            btnAddAlias: '新增別名',
            btnRemoveAlias: '移除別名',
            aliasFieldLabelPattern: '別名 {index}',
            checkboxPrimaryAlias: '主要別名',
            checkboxEnabled: '已啟用',
            assignedUsersLabel: '已指派嘅 Chattenger 用戶',
            assignedUsersHintCreate: '一般用戶多數只得一個郵箱；管理員同超級管理員可以有多個。',
            assignedUsersHintEdit: '一般用戶限一個郵箱帳戶；管理員同超級管理員可以持有多個。',
            userOptionRoleLine: '角色：{role}',
            forceAliasReassignCreate: '若別名已喺其他郵箱關聯，則強制重新指派',
            forceConsoleKeyReassignCreate: '若控制台已喺其他郵箱關聯，則強制搬遷綁定',
            forceAliasReassignEdit: '強制重新指派別名',
            forceConsoleKeyReassignEdit: '強制搬遷控制台綁定',
            accountCardEyebrowPattern: '郵箱帳戶 #{id}',
            accountLoginUserLabel: '登入用戶：',
            accountSystemMailboxSuffix: ' · 系統郵箱',
            btnSave: '儲存',
            btnArchive: '封存',
            archiveBlockedConsoleHint: '封存前請先將控制台綁定由呢個郵箱移走。',
            archiveBlockedSystemHint: '系統郵箱帳戶係保留嘅，唔可以喺呢個介面封存。',
            errCreateFallback: '建立郵箱帳戶失敗',
            errSaveFallback: '儲存郵箱帳戶失敗',
            errArchiveFallback: '封存郵箱帳戶失敗',
            errConflictAliasLine: '{value} 已關聯到 {mailboxDisplayName}。',
            errConflictConsoleKeyLine: '{value} 已綁定到 {mailboxDisplayName}。',
            errConflictLoginLine: '登入名 {value} 已被 {mailboxDisplayName} 使用。',
            createSuccessLine: '已建立郵箱 {displayName}。',
            saveSuccessLine: '已儲存郵箱 {displayName}。',
        },
        ciPage: {
            shellTitle: 'CI',
            shellDescription: '揀環境、釘分支，並將 Next.js 部署到穩定運行時根目錄，而唔使重用可變嘅工作區檢出。',
        },
        wireguardPage: {
            shellTitle: '內部 WireGuard',
            shellDescription: '由外部主機睇內部 control-plane WireGuard 對等端、最近握手記錄，並喺管理界面內停用受損嘅操作員對等端。',
            disablePeerDialogTitle: '停用對等端',
            summaryPeersLabel: '對等端',
            summaryMetaLine: '{active} 活躍 · {inactive} 過期 · {never} 從未連線',
            summaryInterfaceLabel: '介面',
            summaryGeneratedLine: '產生於 {time}',
            transportPolicyLabel: '傳輸邊界',
            transportPolicyWireguardCopy: '呢個頁面只管理 intranet/control-plane 路徑嘅 legacy WireGuard 對等端。',
            transportPolicyAmneziaCopy:
                '面向用戶嘅 regular VPN AmneziaWG 主機同 VPN+ VLESS/H1Cloud 係獨立表面；backend/dev-tools 有 transport-aware live 控制之前，呢度唔控制佢哋。',
            transportPolicyMediaWorkerCopy: '用於 /transcribe、/describe 同 Suno 嘅 media worker 唔由呢個頁面管理。',
            snapshotEyebrow: '即時快照',
            snapshotPollCopy: '頁面可見時每 {seconds} 秒輪詢一次。停用會直接用外部主機。',
            peerCountBadge: '{count} 個對等端',
            badgeNoData: '無資料',
            btnRefresh: '重新整理',
            btnRefreshBusy: '重新整理中…',
            errLoadPeers: '無法載入 WireGuard 對等端。',
            tableColPeer: '對等端',
            tableColStatus: '狀態',
            tableColAllowedIps: '允許嘅 IP',
            tableColHandshake: '握手',
            tableColTraffic: '流量',
            tableColEndpoint: '端點',
            tableColAction: '操作',
            peerUnlabeled: '未命名對等端',
            peerNoIdComment: '無對等端 ID 註解',
            trafficRxPrefix: 'RX',
            trafficTxPrefix: 'TX',
            btnDisable: '停用',
            emptyTitle: '無對等端',
            emptyCopy: 'WireGuard 無返回呢個介面嘅對等端設定，或者外部主機唔 reachable。',
            sheetDisableWarning: '會即刻由外部 WireGuard 介面移除呢個對等端。',
            sheetPeerLabel: '對等端',
            sheetStatusLabel: '狀態',
            btnCancel: '取消',
            btnDisablePeer: '停用對等端',
            btnDisablePeerBusy: '停用緊…',
            disablePeerSuccessLine: '已停用對等端 {display}。',
            errDisablePeer: '停用對等端失敗。',
            valueEmDash: '—',
        },
        terminalPage: EN.admin.terminalPage,
        placeholderPage: {
            migrationCardLabel: '遷移進行中',
        },
        mailConsole: {
            shellEyebrow: '管理 / 郵件',
            shellTitle: '支援收件箱',
            shellDescription: '用於客戶同內部流程嘅支援同營運郵箱。',
            inboxPaneEyebrow: '收件箱',
            inboxTitle: '支援郵件',
            inboxDescription: '喺 Chattenger 管理後台入面處理支援同營運郵件。',
            composeLabel: '新郵件',
            composePlaceholder: '撰寫純文字郵件',
            composeReplyHeading: '回覆草稿',
            composeForwardHeading: '轉寄草稿',
            emptyInboxCopy: '收件箱而家係空嘅。',
            emptySelectionCopy: '揀一行收件箱去睇郵件，或者寫新嘅外發草稿。',
            successPrefix: '郵件已接受投遞',
            actionNewEmail: '新郵件',
            actionReply: '回覆',
            actionForward: '轉寄',
            actionDelete: '刪除',
            sendEmail: '發送郵件',
            addAttachments: '新增附件',
            refreshInbox: '重新整理收件箱',
            fieldTo: '收件人',
            fieldCc: '副本',
            fieldBcc: '密件副本',
            fieldSubject: '主旨',
            fieldBody: '正文',
            placeholderTo: 'user@example.com',
            placeholderCc: 'cc@example.com',
            placeholderBcc: 'bcc@example.com',
            placeholderSubject: '主旨',
            attachmentsBadge: '附件',
            attachmentRemoveAffix: ' · 移除',
            previewUnavailable: '暫時冇預覽',
            selectedMessageEyebrow: '已揀郵件',
            selectedMessageEmptyTitle: '尚未揀郵件',
            unknownDate: '未知日期',
            fromLabel: '寄件人',
            toLabel: '收件人',
            ccLabel: '副本',
            messageIdLabel: 'Message-ID',
            deliveryLabel: '投遞',
            htmlOnlyFallback: '呢封郵件只有 HTML 內容。呢度預設唔會顯示原始 HTML。',
            threadLabel: '埋線',
            threadUnknownSubject: '（無主旨）',
            threadDirectionSent: '已寄出',
            threadDirectionReceived: '已接收',
            errLoadMessage: '載入郵件失敗',
            errLoadAttachments: '載入附件失敗',
            errRefreshInbox: '重新整理收件箱失敗',
            errOpenMessage: '打開郵件失敗',
            errDeleteMessage: '刪除郵件失敗',
            errSendEmail: '發送郵件失敗',
            successMovedToPattern: '郵件已移到 {destination}',
            deliveryQueued: '排隊中',
            deliveryDeferred: '已延遲',
            deliverySent: '已寄出',
            deliveryBounced: '退信',
            deliveryExpired: '已過期',
            deliveryUnknown: '未知',
        },
        infrastructure: {
            ...EN.admin.infrastructure,
            operatorBoundaryEyebrow: 'Runtime 邊界',
            operatorBoundaryTitle: 'VPN 同 media 職責',
            operatorBoundaryCopy:
                '呢個頁面只顯示 runtime 依賴。佢唔會建立 VPN 對等端、bootstrap AmneziaWG 主機、執行 quota shaping，或者遷移 media workload。',
            operatorBoundaryInternalWireguardTitle: '內部 WireGuard',
            operatorBoundaryInternalWireguardCopy: 'Plain WireGuard 仍然係操作員 intranet/control-plane 路徑，唔應該呈現成面向用戶嘅 regular VPN。',
            operatorBoundaryRegularVpnTitle: 'Regular VPN: AmneziaWG',
            operatorBoundaryRegularVpnCopy:
                '面向用戶嘅 regular VPN 屬於 pc_vpn 主機上嘅 AmneziaWG，要等 backend/dev-tools live proof 後先啟用；呢個 UI 只標明邊界。',
            operatorBoundaryVpnPlusTitle: 'VPN+: VLESS/H1Cloud',
            operatorBoundaryVpnPlusCopy: 'VPN+ 仍然係獨立嘅 VLESS/H1Cloud 產品路徑，唔由內部 WireGuard 頁面控制。',
            operatorBoundaryMediaWorkerTitle: 'Media worker',
            operatorBoundaryMediaWorkerCopy: '/transcribe、/describe 同 Suno 係未來路由到 pc_worker 嘅工作；呢個頁面唔可以暗示已有 live 遷移控制。',
            operatorBoundaryNoLiveControlBadge: '無 live 控制',
        },
    },
    assistant: {
        ...EN.assistant,
        navLabel: '助手',
        eyebrow: '助手',
        title: '詢問 Chattenger 助手',
        description: '在呢個專用頁面使用助手。佢唔會作為普通聊天打開，亦唔會作為聯絡人出現喺搜尋中。',
        inputLabel: '提示',
        inputPlaceholder: '詢問 Chattenger、工作區或明確嘅網頁任務。',
        submit: '提問',
        submitting: '思考中…',
        pendingLabel: '助手正在輸入…',
        responseLabel: '回覆',
        unavailableTitle: '助手不可用',
        unavailableCopy: '助手暫時不可用。請稍後再試。',
        emptyCopy: '助手回覆取決於後端提供方是否啟用。',
    },
    vpnPlusShareCode: {
        ...EN.vpnPlusShareCode,
        eyebrow: 'VPN+ 分享',
        title: '領取共享嘅 VPN+ 數據',
        description: '用現有 Chattenger 帳號登入，將呢份已保留嘅 VPN+ 額度加入你嘅餘額。',
        codeLabel: '分享碼',
        invalidCodeFallback: '無效代碼',
        readyTitle: '可以領取',
        readyCopy: '呢個代碼只可以用一次。Chattenger 會先驗證，再更新你嘅 VPN+ 餘額。',
        redeemAction: '領取',
        redeemingAction: '領取中…',
        redeemingTitle: '正在檢查代碼',
        redeemingCopy: 'Chattenger 正在驗證呢個 VPN+ 分享碼。',
        redeemedTitle: 'VPN+ 數據已加入',
        redeemedCopy: '共享嘅 VPN+ 額度已加入你嘅帳號。',
        expiredTitle: '代碼已過期',
        expiredCopy: '呢個分享碼已經唔再有效。請叫發送者重新建立一個。',
        usedTitle: '代碼已使用',
        usedCopy: '呢個分享碼已經被領取，唔可以再用。',
        cancelledTitle: '代碼已取消',
        cancelledCopy: '發送者喺領取前取消咗呢個代碼。',
        invalidTitle: '代碼不可用',
        invalidCopy: '呢個分享碼無效或者格式唔正確。',
        featureOffTitle: 'VPN+ 分享不可用',
        featureOffCopy: '呢個帳號而家未啟用 VPN+ 分享碼領取。',
        unavailableTitle: '無法領取',
        unavailableCopy: 'Chattenger 而家無法領取呢個代碼。請稍後再試。',
        copyLink: '複製連結',
        copyLinkSuccess: 'VPN+ 分享連結已複製。',
        copyLinkFailed: '無法複製呢個連結。',
    },
    dashboard: {
        ...EN.dashboard,
        eyebrow: '面板',
        title: '快速、同步，而且喺每個畫面都準備好。',
        recordings: '錄製',
        searchChats: '搜尋聊天',
        searchChatsPlaceholder: '搜尋聊天',
        searchOlderChats: '正在搜尋更早嘅聊天…',
        searchUsers: '搜尋使用者',
        searchUsersPlaceholder: '尋找使用者',
        searchUsersSearching: '正在搜尋使用者…',
        searchUsersEmpty: '找不到使用者',
        quickPeople: '常用聯絡人',
        notifications: '通知',
        notificationsCopy: '啟用通知之後，喺瀏覽器入面都唔會錯過訊息同通話。',
        allowNotifications: '允許通知',
        notificationReady: '通知已就緒',
        refreshPush: '重新整理 Push',
        disablePush: '停用 Push',
        realtimeLabel: '連線',
        realtimeOnline: '在線',
        realtimeConnecting: '連線中…',
        realtimeReconnecting: '重新連線中…',
        realtimeOnlineCopy: '訊息同事件會即時到達。',
        realtimeConnectingCopy: '介面已經可用，連線正喺建立。',
        realtimeReconnectingCopy: '待發送內容會保留，並喺重連後繼續送出。',
        syncLabel: '同步',
        syncReady: '已就緒',
        syncRefreshing: '更新中…',
        syncing: '同步中…',
        saving: '儲存中…',
        hidePreview: '喺通知預覽隱藏訊息文字',
        showPreview: '顯示預覽',
        vpn: 'VPN',
        vpnChecking: '正在檢查瀏覽器安全嘅 VPN 狀態…',
        channel: '頻道',
        conversation: '對話',
        attachments: '附件',
        globalSearchLabel: '搜尋 Chattenger',
        globalSearchTrigger: '搜尋',
        globalSearchPlaceholder: '搜尋聊天、用戶同頻道',
        searchBack: '返回',
        searchEmptyChats: '搵唔到聊天。',
        searchEmptyChannels: '搵唔到頻道。',
        searchFilterAll: '全部',
        searchFilterChannels: '頻道',
        searchFilters: '搜尋篩選',
        noChatsCompact: '暫時冇聊天 :(',
        quickCreate: '快速建立',
        create: '建立',
        createChat: '新聊天',
        createChannel: '新頻道',
        createGroup: '新群組',
        closeSearch: '關閉搜尋',
        closeQuickCreate: '關閉快速建立選單',
        searchInChat: '喺聊天內搜尋',
        searchInChatPlaceholder: '搜尋訊息',
        searchingMessages: '搜尋中…',
        noHits: '冇結果',
        jumpToLatest: '跳到最新',
        queuedSuffix: '排隊中',
        shellCacheCopy: '最近嘅聊天同待發送內容會保留喺本地，所以介面可以即刻返嚟。',
        waitingThreadEyebrow: '未選擇聊天',
        waitingThreadTitle: '請喺左側揀一個對話。',
        waitingThreadCopy: '揀定聊天之後，歷史、搜尋同發送佇列會即刻掛載。',
        noChatsEyebrow: '暫時冇聊天',
        noChatsCopy: '同步完成之後，聊天列表就會顯示喺呢度。',
        encryptedMessage: '加密訊息',
        encryptedThreadEmpty: '所有訊息都已加密',
        conversationPeerOnline: '在線',
        conversationPeerLastSeenRecently: '啱啱在線',
        conversationPeerLastOnlineAtTime: '在線於 {time}',
        conversationPeerLastOnlineDateTime: '在線 {date} {time}',
        messageMediaQueuedImage: '排隊中的圖片',
        messageMediaQueuedVideo: '排隊中的影片',
        messageMediaVideoBadge: '影片',
        messageMediaImageFallback: '圖片 {index}',
        messageMediaVideoFallback: '影片 {index}',
        messageMediaThumbnailFallback: '預覽',
        circleVideoExpandAria: '展開圓形影片',
        circleVideoResumeAria: '繼續圓形影片',
        circleVideoCollapseAria: '收起圓形影片',
        circleVideoPlaceholder: '圓形影片預覽正在載入',
        circleVideoPaused: '已暫停',
        circleVideoLivePreview: '即時預覽',
        circleVideoUnmute: '開啟聲音',
        circleVideoMute: '靜音',
        circleVideoSeekHandleAria: '拖動圓形影片進度',
    },
    calls: {
        groupCall: '群組通話',
        directCall: '直接通話',
        waitingMediaEyebrow: '等待媒體',
        waitingMediaTitle: '房間已經在線。',
        waitingMediaCopy: '打開咪、高鏡頭或者畫面分享即可開始。',
        roomRecordings: '房間錄製',
        recordingsSheetPrivateEyebrow: '只有自己睇到',
        viewRecordings: '檢視錄製',
        callHistory: '通話',
        callHistoryTitle: '通話、錄製同即時活動',
        callHistoryCopy: '喺一個緊湊工作區入面查看最近房間、打開個人錄製同繼續通話活動。',
        callHistoryEmpty: '開始或加入房間後，通話會顯示喺呢度。',
        callHistoryRefresh: '重新整理',
        callHistoryRefreshing: '整理中…',
        callHistoryRecordings: '錄製',
        callHistoryActivities: '活動',
        activities: '活動',
        activitiesMenu: '通話活動',
        scrumPoker: 'Scrum Poker',
        retroBoard: '復盤白板',
        startScrumPoker: '開始 Poker',
        startRetroBoard: '開始復盤',
        activityLoadFailed: '無法載入通話活動。',
        activityCreateFailed: '無法建立活動。',
        activityUpdateFailed: '無法更新活動。',
        noActivities: '呢個房間暫時未有活動。',
        revealCards: '揭示卡牌',
        cardsRevealed: '卡牌已揭示',
        pokerVotePrompt: '揀你嘅卡牌',
        pokerWaitingReveal: '投票會保持隱藏，直到主持人揭示卡牌。',
        pokerAverage: '平均 {value}',
        retroBoardAddNote: '便條',
        retroBoardAddText: '文字',
        retroBoardAddRectangle: '矩形',
        retroBoardAddEllipse: '橢圓',
        retroBoardSave: '儲存白板',
        retroBoardViewOnly: '只可檢視',
        retroBoardMoveHint: '拖動白板平移，用滾輪或按鈕縮放，並拖動元素整理復盤。',
        retroBoardZoomIn: '放大',
        retroBoardZoomOut: '縮小',
        retroBoardResetView: '重設視圖',
        incomingEyebrow: '來電',
        incomingCopy: '等你喺呢個瀏覽器接聽',
        incomingBusyCopy: '切換之前請先結束目前通話',
        incomingBusyLabel: '目前已有另一個通話',
        answer: '接聽',
        decline: '拒絕',
        leave: '離開',
        microphoneLabel: '咪高峰',
        videoLabel: '鏡頭',
        microphoneOn: '咪已開',
        microphoneOff: '咪已閂',
        cameraOn: '鏡頭已開',
        cameraOff: '鏡頭已閂',
        shareScreen: '分享畫面',
        stopShare: '停止分享',
        hideSelf: '隱藏自己',
        showSelf: '顯示自己',
        audioDevices: '音訊裝置',
        videoDevices: '視像裝置',
        microphoneDevice: '咪',
        participantVolume: '參與者音量',
        speakerDevice: '喇叭',
        cameraDevice: '鏡頭',
        backgroundEffects: '背景',
        backgroundOff: '無',
        backgroundBlur: '模糊',
        backgroundLogo: 'Pigeon',
        backgroundUnavailable: '背景效果需要受支援嘅瀏覽器同夠力嘅裝置。',
        backgroundUsesSelectedOutput: '所選背景會套用到其他參與者同通話錄製睇到嘅鏡頭畫面。',
        sameAsSystem: '跟系統一樣',
        audioProcessing: '音訊處理',
        micGain: '咪音量',
        micGainHint: '會套用到即時咪同通話錄製。',
        noiseSuppression: '降噪',
        echoCancellation: '回音消除',
        autoGainControl: '自動增益控制',
        audioOutputUnavailable: '呢個瀏覽器唔支援揀喇叭輸出，會使用系統預設輸出。',
        deviceChangesApplyCopy: '切換咪、喇叭同鏡頭會即時生效，唔使重新加入房間。',
        recordingUsesSelectedDevices: '進行中嘅通話錄製會跟返目前揀嘅咪、鏡頭、咪音量同音訊處理設定。喇叭輸出只會影響呢部裝置上嘅播放。',
        callEndedUnexpectedly: '通話意外結束咗。',
        callParticipantUnavailable: '而家冇人可以接聽，所以通話未能接通。',
        callConnectionUnavailable: '通話服務暫時未能使用，請一分鐘後再試。',
        callParticipantBusy: '對方而家已經喺另一個通話入面。',
        callAnsweredElsewhere: '呢個通話已經喺另一部裝置上接聽。',
        layoutPreviewEyebrow: '版面預覽',
        layoutPreviewCopy: '此預覽只會保留喺目前瀏覽器中，不會啟動真實通話或錄影。',
        switchCamera: '切換鏡頭',
        callMenu: '通話選單',
        devicesAndRecording: '裝置與錄製',
        videoChatParticipants: '視像通話參與者',
        screenSharingMenu: '畫面分享',
        participantsOnCall: '通話中有 {count} 人',
        screenShareActive: '畫面分享進行中',
        screenSharingParticipantLabel: '{name} 正在分享',
        screenShareEmpty: '而家冇人分享畫面。',
        backToMenu: '返回',
        fullscreenPreview: '全屏預覽',
        ongoingCallLabel: '進行中通話',
        ongoingCallParticipants: '{count} 人在線',
        expand: '展開',
        minimizeCall: '縮小通話',
        backToAutomaticLayout: '返回自動版面',
        liveNow: '直播中',
        mutedLabel: '已靜音',
        recordingStart: '開始錄製',
        recordingPause: '暫停錄製',
        recordingResume: '繼續錄製',
        compactActionMore: '更多',
        compactActionMicrophone: '麥克風',
        compactActionCamera: '鏡頭',
        compactActionRecord: '錄製',
        compactActionPause: '暫停',
        compactActionResume: '繼續',
        compactActionSwitchCamera: '切換',
        compactActionLeave: '離開',
        directPreviewSwap: '切換預覽',
        directPreviewHide: '隱藏預覽',
        directPreviewRestore: '還原預覽',
        screenShareUnsupported: '此瀏覽器不支援畫面分享。',
        screenSharePermissionHint: '請允許螢幕擷取以分享畫面或分頁。',
        screenShareCanceled: '畫面分享已取消。',
        screenShareStartFailed: '無法開始畫面分享。',
    },
    assetViewer: {
        dialogLabel: '附件檢視器',
        thumbnailsLabel: '項目',
        open: '打開',
        download: '下載',
        downloadCurrent: '只下載此項目',
        downloadAll: '下載全部項目',
        downloadChoiceTitle: '要下載甚麼？',
        downloadChoiceDescription: '只儲存目前項目，或開始下載此訊息中的全部附件。',
        previous: '上一項',
        next: '下一項',
        videoBadge: '影片',
        pdfBadge: 'PDF',
        fileBadge: '檔案',
    },
    mail: {
        attachments: '附件',
        files: '檔案',
        shellTitle: '郵箱',
        workspaceNavigationLabel: '工作區導覽',
        workspaceTitle: '郵箱',
        mailboxLabel: '郵箱',
        actionNewEmail: '新郵件',
        actionRefreshMailbox: '重新整理',
        actionDeleteMailbox: '刪除郵箱',
        threadsLabel: '埋線',
        searchThreadsLabel: '搜尋埋線',
        filterAllThreads: '全部',
        filterIncomingThreads: '收件',
        filterAttachmentThreads: '附件',
        inboxQuietCopy: '當呢個郵箱收到或寄出郵件後，thread 會顯示喺呢度。',
        inboxQuietEyebrow: '暫時冇郵件 thread',
        inboxQuietSearchCopy: '而家冇 thread 符合呢個搜尋。',
        inboxQuietTitle: '收件箱好靜',
        incoming: '收件',
        noMailboxDefaultCopy: '你嘅預設郵箱會係',
        noMailboxEyebrow: '郵件',
        noMailboxSidebarCopy: '建立第一個郵箱後即可開始收發郵件。',
        noMailboxSidebarTitle: '暫時未有郵箱',
        noMailboxTitle: '你仲未有電子郵箱',
        sent: '已寄出',
        productDraftNavEyebrow: '未儲存草稿',
        productDraftNavTitle: '離開呢個草稿？',
        productDraftNavCopy: '草稿已儲存喺本機，但呢個導覽會離開目前撰寫畫面。',
        productDraftNavStay: '留喺呢度',
        productDraftNavLeave: '離開草稿',
        productMailboxDeleteEyebrow: '郵箱生命週期',
        productMailboxDeleteTitle: '刪除郵箱？',
        productMailboxDeleteCopyGeneric: '喺一般產品介面刪除呢個郵箱。之後可以還原。',
        productMailboxDeleteCopyWithAddress: '喺一般產品介面刪除 {address}。之後可以喺空白狀態還原。',
        productMailboxDeleteKeep: '保留郵箱',
        productMailboxDeleteConfirm: '刪除郵箱',
        productMailboxDeleteBusy: '刪除中…',
        composeLabel: '撰寫',
        composeReplyHeading: '回覆',
        composeForwardHeading: '轉寄',
        composeSendingAsPattern: '以 {address} 身份發送',
        actionCancel: '取消',
        messageLabel: '訊息',
        threadLabel: '埋線',
        noSubject: '（無主旨）',
        threadMessageCountOne: '{count} 則訊息',
        threadMessageCountMany: '{count} 則訊息',
        actionReply: '回覆',
        actionForward: '轉寄',
        actionDeleteMessage: '刪除',
        unknownSender: '未知寄件人',
        unknownDate: '未知日期',
        fromLabel: '寄件人',
        toLabel: '收件人',
        ccLabel: '副本',
        dateLabel: '日期',
        deliveryLabel: '投遞',
        emptyThreadTitle: '請喺左側揀一條埋線',
        emptyThreadCopy: '打開一個對話，或者開始撰寫新嘅外發郵件。',
        errRefreshMailbox: '重新整理郵箱失敗',
        errOpenMailbox: '打開郵箱失敗',
        errOpenThread: '打開埋線失敗',
        actionCreateMailbox: '建立郵箱',
        actionRestoreMailbox: '還原郵箱',
        successMailboxCreated: '郵箱已建立',
        errCreateMailbox: '建立郵箱失敗',
        successMailboxRestored: '郵箱已還原',
        errRestoreMailbox: '還原郵箱失敗',
        successMailboxDeleted: '郵箱已刪除',
        errDeleteMailbox: '刪除郵箱失敗',
        errStageAttachments: '暫存附件失敗',
        successEmailAccepted: '郵件已接受投遞',
        errSendEmail: '發送郵件失敗',
        successMessageMovedToTrash: '郵件已移到垃圾箱',
        errDeleteMessage: '刪除郵件失敗',
        composerAddAttachments: '新增附件',
        composerSend: '發送',
        composerSending: '發送中…',
        composerHint: '將檔案拖放到呢度、貼上螢幕截圖，或者撳 Cmd/Ctrl+Enter 發送。',
        composerRemoveAttachmentAria: '移除 {fileName}',
    },
    recordings: {
        eyebrow: '錄製',
        title: '你喺各裝置上的 owner-private 房間錄製會話。',
        listTitle: '錄製',
        noRecordingsEyebrow: '暫時未有錄製',
        noRecordingsTitle: '你嘅錄製會話會顯示喺呢度。',
        noRecordingsCopy: '喺 Web 或手機開始群組通話錄製後，每個裝置級私人會話都會獨立顯示喺呢度。',
        detailEyebrow: '錄製詳情',
        playback: '播放',
        downloadMp4: '下載 MP4',
        sceneManifest: '場景清單',
        newBadge: '新',
        playbackPending: '呢段錄製仲等緊通話完結或者最後打包完成之後先可以播放。',
        playbackFailed: '呢段錄製喺產物準備期間失敗。',
        previewLabel: '預覽',
        searchRecordingsLabel: '搜尋錄製',
        filterAll: '全部',
        filterReady: '就緒',
        filterActive: '進行中',
        sortRecent: '最近',
        sortChat: '聊天',
        sortStatus: '狀態',
        roomLabel: '房間 #{roomId}',
        chatFallbackLabel: '聊天 {chatId}',
        createdLabel: '建立時間',
        roomStateLabel: '房間狀態',
        roomStateEnded: '通話已結束',
        roomStateRecordingLive: '錄製進行中',
        roomStateNoActive: '冇進行中錄製',
        roomActivityLabel: '房間活動',
        roomActivityCount: '進行中會話：{count}',
        roomActivityIdle: '閒置',
        artifactLabel: '產物',
        durationLabel: '長度',
        durationSeconds: '{seconds} 秒',
        playbackReadyLabel: '可以打開',
        playbackPendingLabel: '未準備好',
        details: '詳情',
        paginationPrevious: '上一頁',
        paginationNext: '下一頁',
        paginationStatus: '第 {current} / {total} 頁',
        lastStartedLabel: '最近開始',
        pausedAtLabel: '暫停於',
        pendingRoomEndLabel: '等待房間結束',
        sealedAtLabel: '封存於',
        artifactReadyLabel: '產物已就緒',
        artifactFailedLabel: '產物失敗',
        sessionIdLabel: '會話 id',
        ownerDeviceLabel: '擁有者裝置',
        ownerOtherDeviceLabel: '其他裝置 #{deviceId}',
        artifactStatusLabel: '產物狀態',
        artifactDurationLabel: '產物長度',
        artifactSizeLabel: '產物大小',
        sizeMegabytes: '{size} MB',
        artifactErrorLabel: '產物錯誤',
        recordedIntervalsLabel: '錄製區段',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'owner-private 錄製',
        copiedLabel: '已複製',
        copyFailedLabel: '複製失敗',
        copyDetailsLabel: '複製詳情',
        playPlaybackLabel: '播放',
        pausePlaybackLabel: '暫停',
        seekPlaybackLabel: '拖動播放位置',
        rewindTenSecondsLabel: '後退 10 秒',
        forwardTenSecondsLabel: '前進 10 秒',
        enterFullscreenLabel: '打開全螢幕',
        exitFullscreenLabel: '離開全螢幕',
        processingTimelineLabel: '處理時間線',
        summaryRecordingLine: '錄製 #{id}',
        summaryRoomLine: '房間 #{roomId}',
        summaryStatusLine: '狀態：{status}',
        summaryArtifactLine: '產物：{artifact}',
    },
    uiKit: {
        shellEyebrow: '管理 / UI 套件',
        title: 'Web UI kit',
        description: 'Chattenger 登入、面板、通話同管理介面共用嘅基礎互動元件。',
        buttonsAndBadges: '按鈕同標記',
        inputsAndSelects: '輸入同選擇',
        structuredInput: '結構化輸入',
        overlayTitle: '抽屜',
        overlayCopy: 'mobile-first overlay 會重用聊天附件瀏覽器嘅同一種外殼處理。',
        openShowcaseSheet: '打開展示抽屜',
        showcaseSheetEyebrow: 'UI 套件',
        showcaseSheetTitle: '展示抽屜',
        showcaseSheetCopy: '抽屜原語係新 Web 客戶端中 mobile-first overlay 嘅共享基礎。',
        actionsCardLabel: '操作',
        demoButtonPrimary: '主按鈕',
        demoButtonSecondary: '次按鈕',
        demoButtonGhost: '幽靈',
        demoButtonCompact: '緊湊',
        badgeDefaultDemo: '預設',
        badgeAccentDemo: '強調',
        badgeMutedDemo: '弱化',
        badgeDangerDemo: '危險',
        selectionCardLabel: '選擇',
        roleSelectLabel: '角色',
        resourcesSelectLabel: '資源',
        resourcesHint: '代表性 CRM 資源選擇。',
        resourceOptionMessages: '訊息',
        resourceOptionStats: '統計',
        resourceOptionPermissions: '權限',
        resourceOptionUsers: '用戶',
        structuredCardLabel: '結構化輸入',
        arrayInputLabel: '通知電郵',
        arrayInputAddLabel: '新增電郵',
        overlaysCardLabel: '浮層',
        tabsShowcaseLabel: 'UI 套件展示',
        catalogNavigationLabel: 'UI 套件目錄分區',
        catalogControlsTab: '控制項',
        catalogFeedbackTab: '狀態同浮層',
        catalogDataTab: '資料同媒體',
        catalogTextFieldLabel: '通知地址',
        catalogTextFieldHint: '同時展示一般、停用同驗證狀態。',
        catalogTextFieldError: '請輸入完整地址。',
        catalogOtpLabel: '一次性代碼',
        catalogSliderLabel: '預覽值',
        catalogArrayPlaceholder: '輸入通知地址',
        catalogRemoveValueLabel: '移除值',
        catalogEmptySelection: '未有選擇',
        catalogSelectAction: '選擇',
        catalogSelectedAction: '已選擇',
        catalogLoadingLabel: '正在載入元件預覽',
        catalogSettingsLabel: '目錄設定',
        catalogToastAction: '顯示提示',
        catalogToastMessage: 'UI 套件預覽操作已完成。',
        catalogTableLabel: '元件狀態表',
        catalogTableColumnComponent: '元件',
        catalogTableColumnState: '狀態',
        catalogTableValueReady: '就緒',
        catalogEmptyStateTitle: '冇示例記錄',
        catalogEmptyStateCopy: '冇示例記錄符合呢個目錄狀態。',
        catalogShowEmptyState: '顯示空白狀態',
        catalogShowPopulatedState: '顯示已填充狀態',
        catalogQrTitle: '目錄示例 QR 碼',
        catalogMediaAction: '打開媒體預覽',
        catalogMediaLabel: '目錄附件預覽',
        catalogMediaMeta: '安全嘅本地目錄示例',
        roleDemoUser: '一般用戶',
        roleDemoAdmin: '管理員',
        roleDemoSuperAdmin: '超級管理員',
    },
};
